import { CopilotConversation } from 'models/copilot/copilotConversation';
import { TenantProcessStatus } from 'models/copilot/tenantProcessStateBase';

// Mirror of ProcessStateKpiReport.Stage (isoplanner-api, PLAN-070)
export enum KpiReportStage {
  GatherInputs = 0,
  Data = 1,
  Pdf = 2,
  Done = 3,
}

// Mirror of KpiReportFormat (isoplanner-api, PLAN-CTI-087): the file the report is written as; not chosen is PDF
export enum KpiReportFormat {
  Pdf = 0,
  Word = 1,
}

// The chart kind a user asked for one KPI of the report: line, area, bar, stackedBar or donut
export class KpiReportChartKind {
  kpiId: number = 0;

  kind: string = '';
}

// The KPI report as an AI process (PLAN-070), mirror of ProcessStateKpiReport: the inputs step 1 gathered, the data
// report job of step 2, the PDF of step 3 and the conversation holding the cards, the charts and the File card.
export class TenantProcessStateKpiReport {
  status: TenantProcessStatus = TenantProcessStatus.NotStarted;

  // the row key the server answered: a negative reservation number before the job exists, the job id after; every
  // state read, the Canceled write and Stop use it, the web never computes one
  entityId: number = 0;

  stage: KpiReportStage = KpiReportStage.GatherInputs;

  // the data report job; undefined until step 2 starts it (a finished job given by the caller is set at once)
  jobId?: number;

  // the row's key until the job exists
  reservationId?: string;

  // the panel's context key the report was started under; its session is made under it
  contextKey?: string;

  // the file the report is written as; undefined until chosen, which the server treats as PDF
  format?: KpiReportFormat;

  kpiIds: number[] = [];

  start?: Date;

  end?: Date;

  instructions?: string;

  chartKinds: KpiReportChartKind[] = [];

  notes?: string;

  conversation: CopilotConversation = new CopilotConversation();

  // the File block of the PDF; a new version replaces it in place
  fileBlockId?: string;

  traceId?: string;

  // the Copilot session of the finished report, made when the panel hands the thread back (PLAN-076)
  sessionId?: number;

  isEqual(other: TenantProcessStateKpiReport): boolean {
    return (
      this.status === other.status &&
      this.entityId === other.entityId &&
      this.stage === other.stage &&
      this.jobId === other.jobId &&
      this.reservationId === other.reservationId &&
      this.contextKey === other.contextKey &&
      this.format === other.format &&
      this.kpiIds.length === other.kpiIds.length &&
      this.kpiIds.every((id, index) => id === other.kpiIds[index]) &&
      this.start?.getTime() === other.start?.getTime() &&
      this.end?.getTime() === other.end?.getTime() &&
      this.instructions === other.instructions &&
      this.chartKinds.length === other.chartKinds.length &&
      this.chartKinds.every(
        (chart, index) => chart.kpiId === other.chartKinds[index].kpiId && chart.kind === other.chartKinds[index].kind,
      ) &&
      this.notes === other.notes &&
      this.fileBlockId === other.fileBlockId &&
      this.traceId === other.traceId &&
      this.sessionId === other.sessionId &&
      // a chart or the File card added, or a new version of the PDF in the same block
      TenantProcessStateKpiReport.getBlockKeys(this).join() === TenantProcessStateKpiReport.getBlockKeys(other).join()
    );
  }

  private static getBlockKeys(state: TenantProcessStateKpiReport): string[] {
    return state.conversation.thread.map(
      (item) => `${item.id}:${(item.contentBlocks ?? []).map((block) => `${block.id}@${block.version}`).join('|')}`,
    );
  }
}
